import { connect, Dispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { dndContextWrapper } from 'utils/dndContextWrapper';
import React from 'react';
import { ApolloError } from '@apollo/client';

import { RECIPIENT_TYPES, clearSelection } from 'reducers/roleBasedMessagingSearch';
import { Backbone$Id } from 'types/typescript/Backbone';
import { Target } from 'entities/Message';
import CurrentUser from 'auth/CurrentUser';
import { Recipient, ComposerOrganization } from 'types/typescript/messagingTypes';

import SplitComposerHeader from './SplitComposerHeader';
import SelectRecipientPanel, { SelectRecipientTabs } from './SelectRecipientPanel';
import SearchTimer from './SearchTimer';
import { ComposerContext } from './ComposerContext';

export type MessageType = 'urgent' | 'message';

type GQLProps = {
  groupsError?: ApolloError;
  groupsLoading?: boolean;
  orgsError?: ApolloError;
  orgsLoading?: boolean;
  organizations?: ComposerOrganization[];
};
export type ReduxDispatchProps = {
  clearSelection: () => void;
};
export type ReduxStateProps = {
  selection: Recipient[];
  loadingGroupChats: boolean;
  loadingIndividualRecipients: boolean;
};
export type GeneratedProps = GQLProps & ReduxDispatchProps & ReduxStateProps;
export type RequiredProps = {
  defaultTab?: SelectRecipientTabs;
  initialTargets?: Target[];
  onSignificantChangeMade?: () => void;
  onDismiss?: () => void;
  onPageChange?: () => void;
  messageType: MessageType;
  shareSdkBodyUrl?: string;
};
export type Props = RequiredProps & GeneratedProps;

type DefaultProps = { messageType: MessageType };

type State = {
  studentIdListCreated: boolean;
  callAllDevices?: boolean;
};

class SplitComposer extends React.Component<Props, State> {
  static defaultProps: DefaultProps = { messageType: 'message' };

  constructor(props: Props) {
    super(props);
    const studentIdListRecipientInSelection = props.selection.some(
      (s) => s.type === RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT
    );

    this.state = {
      studentIdListCreated: studentIdListRecipientInSelection,
    };
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const changed =
      prevState.studentIdListCreated !== this.state.studentIdListCreated ||
      (prevProps.selection.length !== this.props.selection.length && this.props.selection.length);

    if (changed) {
      if (this.props.onSignificantChangeMade) {
        this.props.onSignificantChangeMade();
      }
    }
  }

  groupResultsAreLoading = () => {
    const { orgsLoading, groupsLoading, loadingGroupChats } = this.props;
    return orgsLoading || groupsLoading || loadingGroupChats;
  };
  advanceToComposeMessage = () => {
    if (this.props.onPageChange) {
      this.props.onPageChange();
    }
  };

  handleSuccessfulStudentIdListCreation = () => {
    this.setState({ studentIdListCreated: true });
  };

  render() {
    const {
      groupsError,
      groupsLoading = false,
      selection,
      initialTargets,
      organizations,
      orgsError,
      orgsLoading = false,
      loadingGroupChats,
      loadingIndividualRecipients,
      messageType,
    } = this.props;

    const isUrgentMessage = messageType === 'urgent';

    const initialTarget = initialTargets && initialTargets[0];
    const groupTargetId: Backbone$Id | null | undefined =
      initialTarget && initialTarget.type === 'group_target' ? initialTarget.group_id : null;
    const orgTargetId: Backbone$Id | null | undefined =
      initialTarget && initialTarget.type === 'district_target' ? initialTarget.org_id : null;

    const hasSearchTimer = CurrentUser.getUser().hasFeature('composer_dd_trace.web');

    return (
      <React.Fragment>
        <ComposerContext.Provider
          value={{
            isUrgentMessage,
            messageType: 'message', // note this is default and should be changed here in the future
            // eslint-disable-next-line object-shorthand
            getUIEventContext: function () {
              return {
                message_type: this.messageType,
                is_urgent: this.isUrgentMessage,
                composer_version: 'web_full_page',
              };
            },
          }}
        >
          <SplitComposerHeader messageType={messageType} />
          <SelectRecipientPanel
            onContinue={this.advanceToComposeMessage}
            groupsError={groupsError}
            loadingGroupResults={this.groupResultsAreLoading()}
            loadingPeopleResults={loadingIndividualRecipients}
            organizations={organizations}
            orgsError={orgsError}
            selection={selection}
            onTabChange={this.props.clearSelection}
            onSuccessfulStudentIdListCreation={this.handleSuccessfulStudentIdListCreation}
            defaultTab={this.props.defaultTab}
            initialTargetId={(groupTargetId || orgTargetId) ?? undefined}
            messageType={messageType}
          />
        </ComposerContext.Provider>
        {hasSearchTimer && (
          <SearchTimer
            orgsLoading={orgsLoading}
            groupsLoading={groupsLoading}
            groupChatsLoading={loadingGroupChats}
            individualRecipientsLoading={loadingIndividualRecipients}
            selection={selection}
            page={1}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: any): ReduxStateProps => {
  const { roleBasedMessagingSearch } = state;
  return {
    selection: roleBasedMessagingSearch.selection,
    loadingGroupChats: roleBasedMessagingSearch.loadingGroupChats,
    loadingIndividualRecipients: roleBasedMessagingSearch.loadingIndividualRecipients,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>): ReduxDispatchProps => {
  return bindActionCreators({ clearSelection }, dispatch);
};

const SplitComposerWithData = connect(mapStateToProps, mapDispatchToProps)(SplitComposer);

// we export this for the SplitComposerModal integration test
// because TargetByStudentIdUploadPage expects a DragDropContext
const SplitComposerWithDnD = dndContextWrapper(SplitComposer);

export { SplitComposer, SplitComposerWithDnD };

export default SplitComposerWithData;
